import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ScheduleAvailability } from "@tabipla/shared/types";
import { useSubmitScheduleResponse } from "@/application/hooks/useSchedules";
import { useUpdateTrip } from "@/application/hooks/useTrips";
import type { ScheduleCardContainerProps, ViewMode, SaveStatus } from "./types";
import { ScheduleCard } from "./ScheduleCard";
import { CloseScheduleDialog } from "./CloseScheduleDialog";

/**
 * ScheduleCard container component.
 * Handles data fetching, mutations, and state management.
 * Passes processed data to the ScheduleCard presenter.
 */
export function ScheduleCardContainer({
  schedule,
  tripId,
  isExpanded,
  onToggleExpand,
  onClose,
  onDelete,
  isClosing,
  isDeleting,
}: ScheduleCardContainerProps) {
  const { t } = useTranslation();
  const responseMutation = useSubmitScheduleResponse(tripId);
  const updateTripMutation = useUpdateTrip(tripId);
  const [viewMode, setViewMode] = useState<ViewMode>("matrix");
  const [localResponses, setLocalResponses] = useState<Record<string, ScheduleAvailability>>(
    schedule.myResponses
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedRef = useRef<string>(JSON.stringify(schedule.myResponses));

  // Sync with server response when it changes
  useEffect(() => {
    const serverData = JSON.stringify(schedule.myResponses);
    if (serverData !== lastSavedRef.current) {
      setLocalResponses(schedule.myResponses);
      lastSavedRef.current = serverData;
    }
  }, [schedule.myResponses]);

  // Auto-submit function
  const autoSubmit = useCallback(
    async (responses: Record<string, ScheduleAvailability>) => {
      if (schedule.isClosed) return;

      const responsesStr = JSON.stringify(responses);
      if (responsesStr === lastSavedRef.current) return;

      setSaveStatus("saving");
      try {
        await responseMutation.mutateAsync({
          scheduleId: schedule.id,
          data: { responses },
        });
        lastSavedRef.current = responsesStr;
        setSaveStatus("saved");

        // Haptic feedback on mobile
        if ("vibrate" in navigator) {
          navigator.vibrate(50);
        }

        // Reset status after a short delay
        setTimeout(() => setSaveStatus("idle"), 1500);
      } catch {
        setSaveStatus("idle");
        toast.error(t("schedules.saveError"));
      }
    },
    [schedule.id, schedule.isClosed, responseMutation, t]
  );

  // Handle response change with debounced auto-submit
  const handleResponseChange = useCallback(
    (date: string, status: ScheduleAvailability) => {
      const newResponses = { ...localResponses, [date]: status };
      setLocalResponses(newResponses);

      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Debounce auto-submit (500ms)
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSubmit(newResponses);
      }, 500);
    },
    [localResponses, autoSubmit]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Find the best date(s) based on availability
  const bestDates = useMemo(() => {
    return schedule.dateSummaries
      .sort((a, b) => {
        // Sort by available count desc, then maybe count desc
        if (b.available !== a.available) return b.available - a.available;
        return b.maybe - a.maybe;
      })
      .slice(0, 3)
      .filter((d) => d.available > 0 || d.maybe > 0);
  }, [schedule.dateSummaries]);

  // Handle close button click - show dialog
  const handleCloseClick = useCallback(() => {
    setShowCloseDialog(true);
  }, []);

  // Handle close confirmation
  const handleCloseConfirm = useCallback(
    async (applyToTrip: boolean) => {
      try {
        // Close the schedule
        await onClose();
      } catch {
        // Error handled by onClose mutation, dialog stays open for retry
        return;
      }

      // Schedule closed successfully, now try to update trip if requested
      setShowCloseDialog(false);

      if (applyToTrip && bestDates.length > 0) {
        try {
          const bestDate = bestDates[0].date;
          await updateTripMutation.mutateAsync({
            startDate: bestDate,
          });
          toast.success(t("schedules.appliedToTrip"));
        } catch {
          // Error handled by mutation, schedule is already closed
        }
      }
    },
    [onClose, bestDates, updateTripMutation, t]
  );

  return (
    <>
      <ScheduleCard
        schedule={schedule}
        isExpanded={isExpanded}
        localResponses={localResponses}
        viewMode={viewMode}
        saveStatus={saveStatus}
        bestDates={bestDates}
        isClosing={isClosing}
        isDeleting={isDeleting}
        onToggleExpand={onToggleExpand}
        onClose={handleCloseClick}
        onDelete={onDelete}
        onViewModeChange={setViewMode}
        onResponseChange={handleResponseChange}
      />

      <CloseScheduleDialog
        open={showCloseDialog}
        onOpenChange={setShowCloseDialog}
        bestDate={bestDates[0]?.date}
        isClosing={isClosing || updateTripMutation.isPending}
        onConfirm={handleCloseConfirm}
      />
    </>
  );
}
